import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Newspaper, Video, FileText, ExternalLink, Calendar } from "lucide-react";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { Reveal, Spinner } from "@/components/public/Primitives";
import { useLanguage } from "@/context/LanguageContext";
import { getMedia } from "@/lib/api";

const typeIcon = { article: Newspaper, video: Video, "press release": FileText };

export default function MediaPress() {
  const { data: media = [], isLoading } = useQuery({ queryKey: ["media"], queryFn: getMedia });
  const { tr } = useLanguage();

  return (
    <div data-testid="media-press-page">
      <PageSeo
        slug="media-press"
        title="Media & Press | Girish Kumar Naidu"
        description="Press coverage, interviews, videos and press releases featuring Girish Kumar Naidu's social work and civic vision."
        keywords="Girish Kumar Naidu media, press coverage, interviews, news"
        schemas={[breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Media & Press" }])]}
      />
      <PageHeader
        overline={tr("page.media.overline")}
        title={tr("page.media.title")}
        subtitle={tr("page.media.subtitle")}
        crumbs={[{ name: "Media & Press" }]}
      />

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          {isLoading ? (
            <Spinner />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {media.map((m, i) => {
                const Icon = typeIcon[m.type] || Newspaper;
                return (
                  <Reveal key={m.id} delay={(i % 3) * 0.08}>
                    <a
                      href={m.externalLink || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="group block bg-white rounded-2xl overflow-hidden border border-gray-200 card-lift h-full"
                    >
                      {m.image && (
                        <div className="h-44 overflow-hidden relative">
                          <img src={m.image} alt={m.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          <span className="absolute top-4 left-4 px-3 py-1 rounded-full font-mono text-[10px] font-bold uppercase tracking-wider bg-navy text-white flex items-center gap-1.5">
                            <Icon className="h-3 w-3" /> {m.type}
                          </span>
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-3 font-mono text-[11px] text-gray-500 mb-3">
                          <span className="font-bold uppercase tracking-wider text-civic">{m.mediaName}</span>
                          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {m.date}</span>
                        </div>
                        <h3 className="font-display text-lg font-bold text-navy mb-2 group-hover:text-gold transition-colors line-clamp-2">{m.title}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{m.description}</p>
                        <span className="inline-flex items-center gap-1.5 text-sm font-bold text-navy group-hover:text-gold transition-colors">
                          View <ExternalLink className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </a>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
